import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import styles from "./SourcingIntelligence.module.css";
import SpecGroup from "./SpecGroup";
import SpendCharts, { AnnualSpendTrend } from "./SpendCharts";
import DeliveryPanel from "./DeliveryPanel.jsx";
import YearComparison from "./YearComparison";
import SpecTrendsPanel from "./SpecTrendsPanel";
import { API_ENDPOINT } from "../../../services/api/resources";
import DSelectInput from "../../../components/Dashboard/DashboardInputs/DSelectInput";
import exportSourcingIntelligencePdf from "./exportSourcingIntelligencePdf";

export const Icons = {
  Search: () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  ),
  ChevronDown: ({ className }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className={className}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  Spinner: () => (
    <svg className={styles.spinner} width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8">
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
  TrendUp: ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  ),
  TrendDown: ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
      <polyline points="17 18 23 18 23 12" />
    </svg>
  ),
  Equal: ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <line x1="5" y1="9" x2="19" y2="9" />
      <line x1="5" y1="15" x2="19" y2="15" />
    </svg>
  ),
  Calendar: ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  Clock: ({ size = 13 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  Package: ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="m16.5 9.4-9-5.19" />
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  ),
  Building: ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  CheckCircle: ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
  AlertCircle: ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  HelpCircle: ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  ArrowRight: ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  ),
  History: ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 .49-4.95" />
    </svg>
  ),
  Info: ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  ),
  Download: ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  ),
};

export function parseINR(str) {
  if (str === null || str === undefined || str === "") return 0;
  return parseFloat(String(str).replace(/,/g, "")) || 0;
}

export function fmtINR(num) {
  return "₹" + Number(num || 0).toLocaleString("en-IN");
}

function normalizeToken(t) {
  if (Array.isArray(t)) return t?.[0] || "";
  return t || "";
}

function getBuyerToken(loginData) {
  return (
    loginData?.user?.token ||
    loginData?.user?.user_token ||
    loginData?.buyer_token ||
    loginData?.token ||
    ""
  );
}

function getOrgToken(loginData) {
  return (
    loginData?.organization_token ||
    loginData?.org_token ||
    loginData?.user?.organization_token ||
    loginData?.user?.org_token ||
    loginData?.user?.token ||
    loginData?.user?.user_token ||
    ""
  );
}

export default function SourcingIntelligence() {
  const loginData = useSelector((state) => state.loginData.value);
  const auth_token = loginData?.auth_token;
  const buyer_token = getBuyerToken(loginData);
  const org_token = getOrgToken(loginData);

  const [itemOptions, setItemOptions] = useState({ tokens: [], visibleOption: [] });
  const [selectedLabel, setSelectedLabel] = useState("");
  const [selectedToken, setSelectedToken] = useState("");

  const [loadingItems, setLoadingItems] = useState(true);
  const [itemsError, setItemsError] = useState("");

  const [loading, setLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState("");
  const [data, setData] = useState(null);
  const [activeYear, setActiveYear] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchItems = async () => {
      if (!auth_token || !org_token) {
        setLoadingItems(false);
        setItemsError("Missing session. Please re-login and try again.");
        return;
      }

      setLoadingItems(true);
      setItemsError("");
      try {
        const res = await axios({
          method: "POST",
          url: `${API_ENDPOINT.view_all_item}`,
          data: { org_token },
          headers: { Authorization: `Bearer ${auth_token} ` },
        });

        const list = Array.isArray(res?.data?.response) ? res.data.response : [];
        if (cancelled) return;

        setItemOptions({
          tokens: list.map((i) => i?.token),
          visibleOption: list.map((i) => `${i?.item_name || "Item"} - (${i?.item_code || "-"})`),
        });
        setSelectedLabel((currentLabel) => {
          if (!currentLabel) return "";
          return list.some((i) => `${i?.item_name || "Item"} - (${i?.item_code || "-"})` === currentLabel)
            ? currentLabel
            : "";
        });
        setSelectedToken((currentToken) => {
          if (!currentToken) return "";
          return list.some((i) => i?.token === currentToken) ? currentToken : "";
        });
        if (list.length === 0) setItemsError("No items found.");
      } catch {
        if (cancelled) return;
        setItemOptions({ tokens: [], visibleOption: [] });
        setItemsError("Failed to load items.");
      } finally {
        if (!cancelled) setLoadingItems(false);
      }
    };

    fetchItems();
    return () => {
      cancelled = true;
    };
  }, [auth_token, org_token]);

  const years = useMemo(() => {
    if (!data?.year_wise_analysis) return [];
    return [...data.year_wise_analysis.map((y) => y.year)].sort((a, b) => Number(b) - Number(a));
  }, [data]);

  const yearMap = useMemo(() => {
    if (!data?.year_wise_analysis) return new Map();
    return new Map(data.year_wise_analysis.map((y) => [y.year, y]));
  }, [data]);

  const activeYearData = activeYear ? yearMap.get(activeYear) : null;
  const prevYearData = useMemo(() => {
    if (!activeYear || years.length < 2) return null;
    const idx = years.indexOf(activeYear);
    if (idx < 0) return null;
    const prev = years[idx + 1];
    return prev ? yearMap.get(prev) : null;
  }, [activeYear, years, yearMap]);

  const totalSpendAll = data
    ? data.year_wise_analysis.reduce((s, y) => s + parseINR(y.annual_spend?.total_spend), 0)
    : 0;
  const allVendors = data
    ? [...new Set(data.year_wise_analysis.flatMap((y) => y.itemwise.map((i) => i.vendor_name)))]
    : [];
  const totalPOs = data ? data.year_wise_analysis.reduce((s, y) => s + y.itemwise.length, 0) : 0;
  const allSpecs = data
    ? [...new Set(data.year_wise_analysis.flatMap((y) => y.itemwise.map((i) => i.specification)))]
    : [];

  const fetchAnalysis = async (token) => {
    const normalized = normalizeToken(token);
    if (!normalized) return;

    if (!auth_token || !buyer_token) {
      setAnalysisError("Missing session. Please re-login and try again.");
      return;
    }

    setLoading(true);
    setAnalysisError("");
    setData(null);
    setActiveYear(null);
    try {
      const res = await axios({
        method: "POST",
        url: `${API_ENDPOINT.comparison_item_po_price_history}`,
        data: { buyer_token, org_token, item_tokens: [normalized] },
        headers: { Authorization: `Bearer ${auth_token} ` },
      });

      const payload = res?.data?.response;
      const yearData = Array.isArray(payload?.year_wise_analysis) ? payload.year_wise_analysis : [];
      setData(payload || null);
      const maxYear = yearData.length ? Math.max(...yearData.map((y) => Number(y?.year || 0))) : null;
      setActiveYear(maxYear || null);
    } catch {
      setData(null);
      setActiveYear(null);
      setAnalysisError("Failed to fetch price history for this item.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedToken) return;
    fetchAnalysis(selectedToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedToken]);

  const onExportPdf = async () => {
    if (!data || exporting) return;
    setExporting(true);
    try {
      exportSourcingIntelligencePdf({ data });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.topbar}>
        <div className={styles.topbarLeft}>
          <div className={styles.topbarAccent} />
          <div>
            <div className={styles.topbarTitle}>Sourcing Intelligence</div>
            <div className={styles.topbarSub}>Price History &amp; Spend Analysis</div>
          </div>
        </div>
        {data && (
          <div className={styles.topbarRight}>
            <button className={styles.exportBtn} type="button" onClick={onExportPdf} disabled={exporting || loading}>
              {exporting ? <Icons.Spinner /> : <Icons.Download />}
              {exporting ? "Exporting…" : "Export PDF"}
            </button>

            <span className={`${styles.badge} ${styles.badgePrimary}`}>
              <Icons.Building size={13} />&nbsp;{data.selected_item?.organization_name}
            </span>
            <span className={`${styles.badge} ${styles.badgeSuccess}`}>{years.length} Years of Data</span>
          </div>
        )}
      </div>

      <div className={styles.content}>
        <div className={styles.selectorCard}>
          <div className={styles.fieldGroup}>
            <DSelectInput
              label="Item Name"
              options={itemOptions}
              value={selectedLabel}
              getIDValue={true}
              disabled={loadingItems}
              customClass={styles.dSelectCompact}
              setIDValue={(tok) => {
                const normalized = normalizeToken(tok);
                setSelectedToken(normalized);
              }}
              onChange={(label) => {
                setSelectedLabel(label || "");
                setData(null);
                setAnalysisError("");
              }}
              filterMatchFrom="any"
              filterLimit={300}
            />
          </div>

          <button className={styles.analyzeBtn} onClick={() => fetchAnalysis(selectedToken)} disabled={!selectedToken || loading}>
            {loading ? <Icons.Spinner /> : <Icons.Search />}
            {loading ? "Analysing…" : "Analyse"}
          </button>
        </div>

        {itemsError && !loadingItems && (
          <div className={styles.errorBanner}>
            <Icons.AlertCircle size={16} />
            <span>{itemsError}</span>
          </div>
        )}

        {analysisError && !loading && (
          <div className={styles.errorBanner}>
            <Icons.AlertCircle size={16} />
            <span>{analysisError}</span>
          </div>
        )}

        {loading && (
          <div className={styles.loader}>
            <Icons.Spinner /> Fetching price history &amp; spend data…
          </div>
        )}

        {data && !loading && (
          <div className={styles.fadeUp}>
            <div className={styles.metaHero}>
              <div className={styles.metaHeroLeft}>
                <div className={styles.metaHeroEyebrow}>Item Overview</div>
                <div className={styles.metaHeroTitle}>{data.selected_item?.item_name}</div>
                <div className={styles.metaHeroMeta}>
                  <Icons.Package size={14} />&nbsp;{data.selected_item?.item_code}
                  <span className={styles.metaHeroMetaDot} />
                  <Icons.Building size={14} />&nbsp;{data.selected_item?.organization_name}
                </div>
              </div>
              <div className={styles.metaHeroStats}>
                {[
                  { val: fmtINR(totalSpendAll), lbl: "Total Addressable Spend(All Years)" },
                  { val: totalPOs, lbl: "Purchase Orders Released" },
                  { val: allVendors.length, lbl: "Unique Vendors" },
                  { val: allSpecs.length, lbl: "Spec Variants" },
                ].map((s, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center" }}>
                    {i > 0 && <div className={styles.metaHeroStatDivider} />}
                    <div className={styles.metaHeroStat}>
                      <div className={styles.metaHeroStatVal}>{s.val}</div>
                      <div className={styles.metaHeroStatLbl}>{s.lbl}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.kpiGrid}>
              {data.year_wise_analysis.map((y) => {
                const prev = data.year_wise_analysis.find((yy) => yy.year === y.year - 1);
                const curr = parseINR(y.annual_spend?.total_spend);
                const prevVal = prev ? parseINR(prev.annual_spend?.total_spend) : null;
                const diff = prevVal ? ((curr - prevVal) / prevVal) * 100 : null;
                const isUp = diff !== null && diff > 0;
                const vendors = [...new Set(y.itemwise.map((i) => i.vendor_name))].length;
                return (
                  <div
                    key={y.year}
                    className={`${styles.kpiCard} ${activeYear === y.year ? styles.kpiCardActive : ""}`}
                    onClick={() => setActiveYear(y.year)}
                  >
                    <div className={styles.kpiCardTop}>
                      <span className={styles.kpiYear}>FY {y.year}</span>
                      {diff !== null && (
                        <span className={`${styles.badge} ${isUp ? styles.badgeDanger : styles.badgeSuccess}`}>
                          {isUp ? <Icons.TrendUp /> : <Icons.TrendDown />} {Math.abs(diff).toFixed(1)}%
                        </span>
                      )}
                    </div>
                    <div className={styles.kpiVal}>₹{y.annual_spend?.total_spend}</div>
                    <div className={styles.kpiLbl}>Annual Spend · {y.annual_spend?.currency}</div>
                    <div className={styles.kpiMeta}>
                      <span>{y.itemwise.length} POs</span>
                      <span>·</span>
                      <span>
                        {vendors} vendor{vendors !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <AnnualSpendTrend yearData={data.year_wise_analysis} />

            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitle}>
                <div className={styles.sectionDot} />
                Year-wise PO Details &amp; Spec Price Comparison
              </div>
              <span className={styles.sectionMeta}>Click a year to switch view</span>
            </div>

            <div className={styles.yearTabBar}>
              {years.map((y) => (
                <button
                  key={y}
                  className={`${styles.yearTab} ${activeYear === y ? styles.yearTabActive : ""}`}
                  onClick={() => setActiveYear(y)}
                >
                  {y}
                </button>
              ))}
            </div>

            {activeYearData && (
              <>
                <SpendCharts activeYearData={activeYearData} />

                <SpecTrendsPanel allYears={data.year_wise_analysis} activeYearData={activeYearData} />

                <DeliveryPanel yearData={activeYearData} />

                {prevYearData && <YearComparison currentYear={activeYearData} prevYear={prevYearData} allYears={data.year_wise_analysis} />}

                <div className={styles.sectionHeader}>
                  <div className={styles.sectionTitle}>
                    <div className={styles.sectionDot} />
                    Specification-wise PO Analysis — {activeYear}
                    <span className={`${styles.badge} ${styles.badgePrimary}`}>{activeYearData.itemwise.length} records</span>
                  </div>
                  <div className={styles.sectionMeta}>
                    Annual Spend:&nbsp;
                    <strong style={{ color: "var(--primary)" }}>₹{activeYearData.annual_spend?.total_spend}</strong>
                  </div>
                </div>

                <SpecGroup yearData={activeYearData} allYears={data.year_wise_analysis} activeYear={activeYear} />
              </>
            )}
          </div>
        )}

        {!data && !loading && (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>📊</div>
            <div className={styles.emptyStateTitle}>Select an item to begin analysis</div>
            <div className={styles.emptyStateSub}>
              Choose an item from the dropdown. We'll show price history, spec-wise cross-year comparison, vendor distribution, and delivery performance.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
